import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, DropdownV2, JustifyVariant, SearchBox, Switch, SwitchSize } from '@makyoapp/makyo-ui'

import { IDBTable } from '@/core/db'
import { appIconKeys } from '@/shared/icons'

import { ColumnTypeIcon } from './ColumnTypeIcon'

/**
 * Columns select toggle
 */
export const ColumnsSelectToggle = ({
  values,
  onChange,
  table,
  excludedColumns = [],
}: {
  values: string[]
  onChange: (value: string[]) => void
  table: IDBTable
  excludedColumns?: ColumnTypes[]
}) => {
  const { t } = useTranslation()

  // derived
  const sourceTableColumns = table.GetColumns()
  const sourceTableColumnsCount = sourceTableColumns.length
  const isAllSelected = values.length === sourceTableColumnsCount

  // state
  const [show, setShow] = useState(false)
  const [colSearch, setColSearch] = useState('')

  const filteredColumns = useMemo(() => {
    const tableCols =
      excludedColumns.length > 0
        ? sourceTableColumns.filter((c) => !excludedColumns?.includes(c.type))
        : sourceTableColumns

    return colSearch ? tableCols.filter((c) => c.name.toLowerCase().includes(colSearch.toLowerCase())) : tableCols
  }, [sourceTableColumns, colSearch, excludedColumns])

  const handleToggleSelectAll = () => {
    onChange(isAllSelected ? [] : sourceTableColumns.map((c) => c.id))
  }

  const handleColumnToggle = (colId: string) => {
    const newValues = values.includes(colId) ? values.filter((id) => id !== colId) : [...values, colId]
    onChange(newValues)
  }

  const handleToggle = (open: boolean) => {
    setShow(open)

    // reset state when close
    if (!open) {
      setColSearch('')
    }
  }

  return (
    <DropdownV2 open={show} onOpenChange={handleToggle}>
      <DropdownV2.Trigger asChild>
        <Button
          variant={values.length > 0 ? ButtonVariant.Outlined : ButtonVariant.OutlinedGray}
          icon={<MaterialIcon size={15} icon={appIconKeys.tableColumn} />}
          className='shrink-0 text-[12.5px]'
        >
          {values.length > 0 ? `${values.length} Columns Selected` : t('No Columns Selected')}
        </Button>
      </DropdownV2.Trigger>

      <DropdownV2.Content className='z-302 w-[250px] text-m-m' side='bottom' align='start'>
        {/* search */}
        <div className='border-b bdr font-normal'>
          <SearchBox
            value={colSearch}
            onSearch={(search) => {
              setColSearch(search)
            }}
            onClearSearch={() => setColSearch('')}
            noBorder
          />
        </div>

        <div className='font-normal py-2 min-h-[200px] max-h-[345px] overflow-y-scroll'>
          {/* toggle all */}
          <label className='flex-c mk-dd-row cursor-pointer'>
            <div className='flex-c w-4/5'>{t('Select/Unselect All')}</div>
            <div>
              <Switch
                checked={isAllSelected}
                justify={JustifyVariant.Start}
                size={SwitchSize.XS}
                onClick={handleToggleSelectAll}
              />
            </div>
          </label>

          {/* columns list */}
          {filteredColumns.map((column) => {
            return (
              <label className='flex-c mk-dd-row cursor-pointer' key={column.id}>
                <div className='flex-c w-4/5'>
                  <ColumnTypeIcon type={column.type} />
                  <div className='max-w-[160px] txt'>{column.name}</div>
                </div>
                <div>
                  <Switch
                    checked={values.includes(column.id)}
                    justify={JustifyVariant.Start}
                    size={SwitchSize.XS}
                    onClick={() => {
                      handleColumnToggle(column.id)
                    }}
                  />
                </div>
              </label>
            )
          })}
        </div>
      </DropdownV2.Content>
    </DropdownV2>
  )
}
